import { SITE_STATUS_META } from './lens'

import type { SiteIssueCount } from './issues'
import type { GisSite } from '../api/types'
import type Feature from 'ol/Feature'

/**
 * Hover-tooltip HTML for a single site marker — name + status, plus the open/critical issue counts
 * when the issue overlay is active. Pure string builder (the map sets it via innerHTML).
 */
export function siteTipHtml(name: string, statusLabel: string, count?: SiteIssueCount): string {
  if (!count || count.open === 0) {
    return `<b>${name}</b><br>${statusLabel}`
  }
  const crit = count.critical > 0 ? ` · ${count.critical} critical` : ''
  return `<b>${name}</b><br>${statusLabel} · ${count.open} open${crit}`
}

/** Hover-tooltip HTML for a cluster bubble — N sites, with aggregate open/critical counts when present. */
export function clusterTipHtml(siteCount: number, totals: SiteIssueCount): string {
  if (totals.open === 0) {
    return `<b>${siteCount} sites</b><br>${siteCount} sites in view`
  }
  const crit = totals.critical > 0 ? ` · ${totals.critical} critical` : ''
  return `<b>${siteCount} sites</b><br>${totals.open} open${crit}`
}

/**
 * Tooltip HTML for a hovered site/cluster marker (OL Cluster feature) — delegates to the pure builders
 * above, aggregating the cluster's open/critical issue counts from the overlay.
 */
export function siteClusterTip(members: Feature[], overlay?: Map<string, SiteIssueCount>): string {
  if (members.length === 1) {
    const s = members[0]!.get('site') as GisSite
    return siteTipHtml(s.name, SITE_STATUS_META[s.status].label, overlay?.get(s.id))
  }
  let open = 0
  let critical = 0
  if (overlay) {
    for (const f of members) {
      const c = overlay.get((f.get('site') as GisSite).id)
      if (c) {
        open += c.open
        critical += c.critical
      }
    }
  }
  return clusterTipHtml(members.length, { open, critical })
}
